import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import { X, Plus, Tag, Edit2, Trash2 } from 'lucide-react'

const ClientTags = ({ clientId, selectedTags = [], onChange }) => {
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingTag, setEditingTag] = useState(null)
  const [tagName, setTagName] = useState('')
  const [tagColor, setTagColor] = useState('#3B82F6')
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (organizationId) {
      fetchTags()
      fetchClientTags()
    }
  }, [organizationId, clientId])

  const fetchTags = async () => {
    if (!organizationId) return
    try {
      const { data, error } = await supabase
        .from('client_tags')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name')

      if (error) throw error
      setTags(data || [])
    } catch (error) {
      console.error('Error fetching tags:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClientTags = async () => {
    if (!clientId || !organizationId) return
    try {
      const { data, error } = await supabase
        .from('client_tag_assignments')
        .select('tag_id')
        .eq('client_id', clientId)

      if (error) throw error
      const tagIds = data?.map((a) => a.tag_id) || []
      if (onChange) {
        onChange(tagIds)
      }
    } catch (error) {
      console.error('Error fetching client tags:', error)
    }
  }

  const handleCreateTag = async () => {
    if (!tagName.trim() || !organizationId) return
    try {
      const { data, error } = await supabase
        .from('client_tags')
        .insert({
          organization_id: organizationId,
          name: tagName.trim(),
          color: tagColor,
        })
        .select()
        .single()

      if (error) throw error
      setTags([...tags, data])
      setTagName('')
      setTagColor('#3B82F6')
      setShowCreateForm(false)
    } catch (error) {
      console.error('Error creating tag:', error)
      alert(error.message || 'Failed to create tag')
    }
  }

  const handleUpdateTag = async () => {
    if (!tagName.trim() || !editingTag) return
    try {
      const { data, error } = await supabase
        .from('client_tags')
        .update({ name: tagName.trim(), color: tagColor })
        .eq('id', editingTag.id)
        .select()
        .single()

      if (error) throw error
      setTags(tags.map((t) => (t.id === editingTag.id ? data : t)))
      setEditingTag(null)
      setTagName('')
      setTagColor('#3B82F6')
    } catch (error) {
      console.error('Error updating tag:', error)
      alert(error.message || 'Failed to update tag')
    }
  }

  const handleDeleteTag = async (tagId) => {
    if (!confirm('Delete this tag? It will be removed from all clients.')) return
    try {
      const { error } = await supabase.from('client_tags').delete().eq('id', tagId)
      if (error) throw error
      setTags(tags.filter((t) => t.id !== tagId))
    } catch (error) {
      console.error('Error deleting tag:', error)
      alert(error.message || 'Failed to delete tag')
    }
  }

  const toggleTag = async (tagId) => {
    if (!clientId || !organizationId) return
    const isSelected = selectedTags.includes(tagId)
    try {
      if (isSelected) {
        const { error } = await supabase
          .from('client_tag_assignments')
          .delete()
          .eq('client_id', clientId)
          .eq('tag_id', tagId)
        if (error) throw error
      } else {
        const { data: { user } } = await supabase.auth.getUser()
        const { error } = await supabase.from('client_tag_assignments').insert({
          client_id: clientId,
          tag_id: tagId,
          assigned_by: user?.id,
        })
        if (error) throw error
      }
      fetchClientTags()
    } catch (error) {
      console.error('Error toggling tag:', error)
      alert(error.message || 'Failed to update tag assignment')
    }
  }

  const openEditForm = (tag) => {
    setEditingTag(tag)
    setTagName(tag.name)
    setTagColor(tag.color)
    setShowCreateForm(true)
  }

  const closeForm = () => {
    setShowCreateForm(false)
    setEditingTag(null)
    setTagName('')
    setTagColor('#3B82F6')
  }

  if (loading) return <div className="text-sm text-gray-500">Loading tags...</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Tag size={20} />
          Tags & Categories
        </h3>
        {canWrite('clients') && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            New Tag
          </Button>
        )}
      </div>

      {/* Create/Edit Tag Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card variant="soft" className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">
                  {editingTag ? 'Edit Tag' : 'Create New Tag'}
                </h4>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-3">
                <Input
                  label="Tag Name"
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  placeholder="e.g. VIP, Follow-up, Chronic"
                />
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Color</label>
                  <input
                    type="color"
                    value={tagColor}
                    onChange={(e) => setTagColor(e.target.value)}
                    className="w-full h-10 rounded-lg border-2 border-gray-200 cursor-pointer"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={editingTag ? handleUpdateTag : handleCreateTag}
                    disabled={!tagName.trim()}
                    className="flex-1"
                  >
                    {editingTag ? 'Update' : 'Create'}
                  </Button>
                  <Button variant="secondary" onClick={closeForm} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Available Tags */}
      {tags.length === 0 ? (
        <p className="text-sm text-gray-500">No tags created yet. Create your first tag above.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => {
            const isSelected = selectedTags.includes(tag.id)
            return (
              <motion.button
                key={tag.id}
                onClick={() => clientId && canWrite('clients') && toggleTag(tag.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isSelected
                    ? 'ring-2 ring-offset-2'
                    : 'opacity-60 hover:opacity-100'
                } ${clientId && canWrite('clients') ? 'cursor-pointer' : 'cursor-default'}`}
                style={{
                  backgroundColor: isSelected ? tag.color : `${tag.color}20`,
                  color: isSelected ? '#fff' : tag.color,
                  ringColor: tag.color,
                }}
              >
                <div className="flex items-center gap-2">
                  <span>{tag.name}</span>
                  {canWrite('clients') && !clientId && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          openEditForm(tag)
                        }}
                        className="hover:bg-white/20 rounded p-0.5"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteTag(tag.id)
                        }}
                        className="hover:bg-white/20 rounded p-0.5"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </motion.button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ClientTags

